import { mapGetters, mapActions, mapState, mapMutations } from 'vuex';
import { isValueExist, showAuthLink } from '@util';
import { CONNECTION_TYPES } from '@consts';
import { AssetService } from '@/services';

const { FB_PAGE } = CONNECTION_TYPES;

import FBPagesSelect from '@/components/Common/FBPagesSelect.js';
import Dialog from '@components/Common/Dialog';

export default {
    name: 'FbPageShareModal',
    props: {
        active: {
            type: Boolean,
            default: false,
        },
        token: {
            type: String | Number,
            required: true,
        },
    },
    computed: {
        ...mapGetters('user', ['fbInfo']),
        ...mapState('user', ['authCode']),
        _active: {
            get: R.prop('active'),
            set(value) {
                this.$emit('update:active', value);
            },
        },
        status() {
            if (this.copyLink) {
                return 'tryAnother';
            }

            return R.cond([
                [
                    R.pathSatisfies(R.equals(0), ['pages', 'length']),
                    R.always('noPages'),
                ],
                [R.isEmpty, R.always('notAuth')],
                [R.T, R.always('pages')],
            ])(this.fbInfo);
        },
        confirmText() {
            switch (this.status) {
                case 'noPages':
                    return this.$t('CONNECTION.try_with_another_account');
                case 'tryAnother':
                    return this.$t('COMMON.close');
                case 'notAuth':
                    return this.$t('COMMON.login_to_facebook');
                case 'pages':
                    return this.$t('COMMON.select');
                default:
                    break;
            }
        },
    },
    data() {
        return {
            selected: null,
            copyLink: false,
            loadingProgress: false,
            removeFn: R.identity,
        };
    },
    render() {
        const props = {
            attrs: {
                active: true,
                confirmText: this.confirmText,
                cancelText: this.$t('COMMON.cancel'),
                hasCancelButton: !R.test(/tryAnother/gi, this.status),
                loadingProgress: this.loadingProgress,
            },
            on: {
                'update:active': (value) => (this._active = value),
                confirm: () => {
                    switch (this.status) {
                        case 'noPages':
                            this.copyLink = true;
                            break;
                        case 'tryAnother':
                            this._active = false;
                            break;
                        case 'notAuth':
                            this.loadingProgress = true;
                            this.removeFn = showAuthLink({
                                type: FB_PAGE,
                                cb: (event) =>
                                    this.fetchAssetThroughLink(event).finally(
                                        () => {
                                            this.loadingProgress = false;
                                            this.removeFn();
                                        }
                                    ),
                                cancelCb: () => (this.loadingProgress = false),
                            });
                            break;
                        case 'pages':
                            if (isValueExist(this.selected)) {
                                this.loadingProgress = true;
                                AssetService.addAssets({
                                    queryObj: R.pick(['token'], this),
                                    assets: [
                                        {
                                            type: FB_PAGE,
                                            data: {
                                                ...this.selected,
                                                facebookUserToken: this.fbInfo
                                                    .longLifeToken,
                                            },
                                            code: this.authCode,
                                        },
                                    ],
                                })
                                    .then(this.inputEmitter)
                                    .catch(console.error)
                                    .finally(
                                        () => (this.loadingProgress = false)
                                    );
                                break;
                            } else {
                                return this.toast('please select facebook app');
                            }
                        default:
                            break;
                    }
                },
                cancel: () => {
                    this.setFbInfo({});
                    this._active = false;
                },
            },
        };

        return (
            <Dialog {...props}>
                <FBPagesSelect
                    fbInfo={this.fbInfo}
                    onUpdate={(value) => {
                        this.selected = value;
                    }}
                    status={this.status}
                    token={this.token}
                />
            </Dialog>
        );
    },
    methods: {
        ...mapActions('user', ['fetchAssetThroughLink']),
        ...mapMutations('user', ['setFbInfo']),
    },
    destroyed() {
        this.removeFn();
    },
};
